import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "./Header";
import SEO from "./SEO";
import "./CheckoutSuccess.css";
import { needsProfileCompletion } from "../utils/profileCompletion";
import { getMyDirectoryListing } from "../services/directoryService";
import {
  normalizeUserForAnalytics,
  trackPurchase,
  trackSubscriptionConversion,
} from "../utils/analytics";

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyToken, user } = useAuth();
  const sessionId = searchParams.get("session_id");
  const purchaseType = searchParams.get("purchase_type");
  const PAYMENT_FLOW_FLAG = "lawfusion_stripe_flow_completed";

  // status: "loading" | "success" | "error"
  const [status, setStatus] = useState("loading");
  const [subscription, setSubscription] = useState(null);
  const [tokenTopUp, setTokenTopUp] = useState(null); // for token top-up results
  const [errorMessage, setErrorMessage] = useState("");
  // Featured Directory Placement comes with every paid plan, so a new
  // subscriber is pointed at whichever step is actually theirs: finish the
  // Featured listing they already have, or set one up.
  const [directoryNext, setDirectoryNext] = useState(null);
  const clearPaymentFlowFlag = () => {
    sessionStorage.removeItem(PAYMENT_FLOW_FLAG);
  };

  useEffect(() => {
    if (status !== "success" || purchaseType === "token_topup") return;

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const listing = await getMyDirectoryListing(authHeaders);
        if (!cancelled) setDirectoryNext(listing ? "listing" : "join");
      } catch (err) {
        // The directory prompt is a bonus, never a reason to break this page.
        if (!cancelled) setDirectoryNext(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, purchaseType]);

  useEffect(() => {
    if (!sessionId) {
      setErrorMessage("No session ID found. Please contact support.");
      setStatus("error");
      return;
    }

    const API_URL =
      process.env.REACT_APP_API_URL || "http://localhost:5000/api";

    if (purchaseType === "token_topup") {
      // --- Token Top-Up confirm flow ---
      const confirmTokenPurchase = async () => {
        try {
          const response = await axios.post(
            `${API_URL}/stripe/confirm-token-purchase`,
            { sessionId },
          );
          if (response.data?.success) {
            const refreshedUser = await verifyToken();
            const userForAnalytics =
              normalizeUserForAnalytics(refreshedUser) ||
              response.data.analytics?.user;
            await trackPurchase(
              response.data.analytics,
              userForAnalytics,
              response.data.analytics?.eventId,
            );
            setTokenTopUp(response.data);
            setStatus("success");
          } else {
            setErrorMessage(
              response.data?.message ||
              "Token purchase could not be confirmed.",
            );
            setStatus("error");
          }
        } catch (error) {
          const msg =
            error.response?.data?.message ||
            "Something went wrong while confirming your token purchase. Please contact support.";
          setErrorMessage(msg);
          setStatus("error");
        }
      };
      confirmTokenPurchase();
    } else {
      // --- Subscription confirm flow (existing) ---
      const confirmPayment = async () => {
        try {
          const response = await axios.post(
            `${API_URL}/stripe/confirm-payment`,
            {
              sessionId,
            },
          );

          if (response.data?.success) {
            const refreshedUser = await verifyToken();
            setSubscription(response.data.subscription);
            const userForAnalytics =
              normalizeUserForAnalytics(refreshedUser) ||
              response.data.analytics?.user ||
              normalizeUserForAnalytics(user);
            await trackSubscriptionConversion(
              response.data.analytics,
              userForAnalytics,
              response.data.analytics?.eventId,
            );
            setStatus("success");
          } else {
            setErrorMessage(
              response.data?.message || "Payment could not be confirmed.",
            );
            setStatus("error");
          }
        } catch (error) {
          const msg =
            error.response?.data?.message ||
            "Something went wrong while confirming your payment. Please contact support.";
          setErrorMessage(msg);
          setStatus("error");
        }
      };
      confirmPayment();
    }
  }, [sessionId, purchaseType, verifyToken]);

  // Prevent browser Back from taking the user back into the payment/checkout flow
  // after Stripe succeeds (Back can revisit older history entries).
  useEffect(() => {
    if (status !== "success") return;

    // Mark this browser session as having completed Stripe checkout.
    sessionStorage.setItem(PAYMENT_FLOW_FLAG, String(Date.now()));

    // Push an extra history entry so that hitting Back triggers `popstate`,
    // letting us immediately redirect away from the payment success screen.
    window.history.pushState(
      { [PAYMENT_FLOW_FLAG]: true },
      "",
      window.location.href,
    );

    const onPopState = () => {
      clearPaymentFlowFlag();
      navigate(needsProfileCompletion(user) ? "/complete-profile" : "/research", {
        replace: true,
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [status, user, navigate, purchaseType]);

  // --- Loading State ---
  if (status === "loading") {
    return (
      <div className="checkout-success-page">
        <Header />
        <div className="checkout-success-container">
          <div className="checkout-success-loading">
            <div className="spinner"></div>
            <p>
              {purchaseType === "token_topup"
                ? "Crediting your tokens..."
                : "Activating your subscription..."}
            </p>
            <div
              style={{ marginTop: "8px", fontSize: "0.9rem", color: "#666" }}
            >
              Please do not refresh the page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Error State ---
  if (status === "error") {
    return (
      <div className="checkout-success-page">
        <Header />
        <div className="checkout-success-container">
          <div className="checkout-success-card">
            <div
              className="checkout-success-icon"
              style={{
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                boxShadow: "0 10px 30px rgba(239, 68, 68, 0.3)",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h1 style={{ color: "#dc2626" }}>Payment Not Confirmed</h1>
            <p className="checkout-success-message">{errorMessage}</p>
            <div className="checkout-success-actions">
              <button
                className="checkout-success-btn"
                onClick={() => {
                  clearPaymentFlowFlag();
                  navigate("/pricing");
                }}
              >
                Try Again
              </button>
              <button
                className="checkout-success-btn-secondary"
                onClick={() => {
                  clearPaymentFlowFlag();
                  navigate("/research");
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Success State: Token Top-Up ---
  if (status === "success" && purchaseType === "token_topup") {
    // Fallback to 25 if the backend didn't return a count or returned 0
    const tokensAdded = (tokenTopUp?.tokensAdded && tokenTopUp.tokensAdded > 0)
      ? tokenTopUp.tokensAdded
      : 25;
    const newLimit = tokenTopUp?.newAiMonthlyLimit;
    return (
      <div className="checkout-success-page">
        <Header />
        <div className="checkout-success-container">
          <div className="checkout-success-card">
            <div
              className="checkout-success-icon"
              style={{
                background: "linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)",
                boxShadow: "0 10px 30px rgba(6, 182, 212, 0.35)",
              }}
            >
              {/* Lightning bolt icon */}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 2L4.09 12.96a.5.5 0 0 0 .41.79H11l-1 8.25L19.91 11a.5.5 0 0 0-.41-.75H13l1-8.25z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <h1>Tokens Added!</h1>
            <p className="checkout-success-message">
              {tokensAdded > 0
                ? `${tokensAdded} AI action tokens have been added to your account.`
                : "Your tokens are already credited to your account."}
            </p>

            <div className="checkout-plan-details">
              <div className="plan-detail-item">
                <span className="plan-detail-label">Tokens Added:</span>
                <span className="plan-detail-value">{tokensAdded} tokens</span>
              </div>
              {newLimit != null && (
                <div className="plan-detail-item">
                  <span className="plan-detail-label">New Monthly Limit:</span>
                  <span className="plan-detail-value">{newLimit} actions</span>
                </div>
              )}
            </div>

            <p className="checkout-success-submessage">
              Your new token balance is reflected in your account immediately.
            </p>

            <div className="checkout-success-actions">
              <div className="checkout-success-primary-actions">
                <button
                  className="checkout-success-btn"
                  onClick={() => {
                    clearPaymentFlowFlag();
                    navigate("/research");
                  }}
                >
                  Start Research
                </button>
                <button
                  className="checkout-success-btn"
                  onClick={() => {
                    clearPaymentFlowFlag();
                    navigate("/debate");
                  }}
                >
                  Start Debate
                </button>
              </div>
              <button
                className="checkout-success-btn-secondary"
                onClick={() => {
                  clearPaymentFlowFlag();
                  navigate("/settings?tab=billing");
                }}
              >
                View Billing
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Success State: Subscription ---
  const planName = subscription?.plan || "Essentials";
  const planDisplayName = planName.charAt(0).toUpperCase() + planName.slice(1);
  // Always show the fixed trial limit (20 AI Actions) on the trial success screen.
  const TRIAL_AI_ACTIONS_LIMIT = 20;
  const aiLimit = subscription?.status === "trialing"
    ? TRIAL_AI_ACTIONS_LIMIT
    : (subscription?.aiMonthlyLimit || TRIAL_AI_ACTIONS_LIMIT);
  const maxProjects = subscription?.maxSavedProjects || 5;
  const profileIncomplete = needsProfileCompletion(user);

  return (
    <div className="checkout-success-page">
      <SEO title="Payment Successful" path="/stripe/success" description="Your LawFusion subscription is active." noIndex={true} />
      <Header />
      <div className="checkout-success-container">
        <div className="checkout-success-card">
          <div className="checkout-success-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1>Payment Successful!</h1>
          <p className="checkout-success-message">
            Welcome to LawFusion {planDisplayName} Plan
          </p>

          <div className="checkout-plan-details">
            <div className="plan-detail-item">
              <span className="plan-detail-label">Plan:</span>
              <span className="plan-detail-value">{planDisplayName}</span>
            </div>
            <div className="plan-detail-item">
              <span className="plan-detail-label">Trial Period:</span>
              <span className="plan-detail-value">7 Days Free</span>
            </div>
            <div className="plan-detail-item">
              <span className="plan-detail-label">Trial Limit:</span>
              <span className="plan-detail-value">{aiLimit} AI Actions</span>
            </div>
          </div>

          <p className="checkout-success-submessage">
            Your 7-day free trial has started. You won't be charged until the
            trial ends. Start researching now!
          </p>



          {directoryNext && (
            <div className="checkout-featured-note">
              <p className="checkout-featured-title">
                Featured Directory Placement is included with your plan
              </p>
              <p className="checkout-featured-copy">
                {directoryNext === "listing"
                  ? "Your directory listing has been upgraded to Featured. Add your photo to finish it."
                  : "Create your free directory listing and it will go live as Featured once approved."}
              </p>
              <button
                type="button"
                className="checkout-success-btn-secondary"
                onClick={() => {
                  clearPaymentFlowFlag();
                  // Straight to the edit form, not the listing overview: the
                  // one thing left to do is add the photo, and landing on the
                  // overview made them find their own way there.
                  navigate(
                    directoryNext === "listing"
                      ? "/my-listing/edit"
                      : "/directory?join=1",
                  );
                }}
              >
                {directoryNext === "listing"
                  ? "Complete My Featured Listing"
                  : "Create My Directory Listing"}
              </button>
            </div>
          )}

          <div className="checkout-success-actions">
            <div className="checkout-success-primary-actions">
              <button
                className="checkout-success-btn"
                onClick={() => {
                  clearPaymentFlowFlag();
                  // If user is missing profile info (e.g. Google OAuth), prompt them first
                  navigate(
                    profileIncomplete ? "/complete-profile" : "/research",
                    profileIncomplete
                      ? { state: { from: "/research" } }
                      : undefined,
                  );
                }}
              >
                Start Research
              </button>
              <button
                className="checkout-success-btn"
                onClick={() => {
                  clearPaymentFlowFlag();
                  navigate(
                    profileIncomplete ? "/complete-profile" : "/debate",
                    profileIncomplete ? { state: { from: "/debate" } } : undefined,
                  );
                }}
              >
                Start Debate
              </button>
            </div>
            <button
              className="checkout-success-btn-secondary"
              onClick={() => {
                clearPaymentFlowFlag();
                navigate("/settings");
              }}
            >
              Manage Subscription & Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
